import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, UploadCloud, FileText, X, Sparkles, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TRACK_OPTIONS, BASES, IMAGES } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/context/AuthContext";
import { ScanningOverlay, ScanResultModal } from "@/components/ResumeScan";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXP = ["No experience (entry)", "Some maritime experience", "Experienced tradesperson"];

export default function ApplyForm() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "", target_track: TRACK_OPTIONS[0], preferred_location: BASES[0], experience_level: EXP[0], availability: "" });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | scanning | done
  const [result, setResult] = useState(null);
  const [firstName, setFirstName] = useState("");
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, full_name: user.name || f.full_name, email: user.email || f.email, phone: user.phone || f.phone }));
  }, [user]);

  const pickFile = (f) => {
    if (!f) return;
    const ok = /\.(pdf|docx|doc|txt)$/i.test(f.name);
    if (!ok) { toast.error("Resume must be a PDF, DOCX or TXT file."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Resume is too large (max 10MB)."); return; }
    setResume(f);
  };

  const pollScan = async (id) => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await axios.get(`${API}/apply/${id}/scan`);
        if (data.status === "done") { setResult(data.scan); setFirstName(data.first_name || ""); setPhase("done"); return; }
        if (data.status === "failed") { setPhase("idle"); toast.error("We couldn't read that resume, but your application was received. A recruiter will reach out."); return; }
      } catch { /* keep polling */ }
    }
    setPhase("idle");
    toast.info("Your application was received — your AI report is still processing and will be ready shortly.");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) { toast.error("Name, email and phone are required."); return; }
    if (!user && (!form.password || form.password.length < 6)) { toast.error("Create a password (min 6 characters) to start your application."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      ["full_name", "email", "phone", "target_track", "preferred_location", "experience_level", "availability"].forEach((k) => fd.append(k, form[k]));
      if (!user) fd.append("password", form.password);
      if (resume) fd.append("resume", resume);

      const { data } = await axios.post(`${API}/apply`, fd);
      await refresh();

      if (data.scan_status === "processing") {
        setPhase("scanning");
        pollScan(data.application_id);
      } else {
        toast.success("Application received — welcome aboard", { description: resume ? "We couldn't fully read that resume, but a recruiter will review it and reach out." : "Attach a resume next time for an instant AI job-match report." });
      }
      setForm((f) => ({ ...f, password: "", availability: "" }));
      setResume(null);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="apply" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.drydockVessel} alt="" className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F14] via-[#0B0F14]/85 to-[#0B0F14]" />
        <div className="absolute inset-0 blueprint-grid opacity-30" />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
          <div className="font-mono-tech text-[11px] tracking-[0.25em] text-[#FF5E1E] mb-3">[ SEC—INTAKE — START MY APPLICATION ]</div>
          <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl">Get shipyard ready</h2>
          <p className="mt-4 text-[#8FA3B8]">Firewatch → OSHA → DBIDS → Placement → Career Advancement. Start with a 10-minute application — no experience required.</p>
        </motion.div>

        {/* AI resume marketing banner */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mb-6 flex items-start gap-3 bg-gradient-to-r from-[#FF5E1E]/15 to-transparent border border-[#FF5E1E]/30 p-4">
          <Sparkles size={20} className="text-[#FF5E1E] mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-black uppercase text-sm sm:text-base">Upload your resume — our AI finds your best-paying shipyard trades</div>
            <p className="text-[13px] text-[#B8C4D0] mt-0.5">Reentry AI scans your experience and instantly maps you to matching jobs, a career pathway, and earning potential.</p>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
          onSubmit={submit} data-testid="apply-form"
          className="bg-[#10161F]/90 backdrop-blur-xl border border-white/10 p-6 sm:p-8 ember-glow"
        >
          {user && (
            <div className="mb-5 flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.14em] text-[#10B981] uppercase"><ShieldCheck size={14} /> Signed in as {user.name} — this application saves to your passport</div>
          )}

          {/* Resume dropzone */}
          <div className="mb-5">
            <Label className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] uppercase mb-1.5 block">Resume <span className="text-[#FF5E1E]">· AI scan</span> (PDF / DOCX · optional)</Label>
            {resume ? (
              <div data-testid="resume-selected" className="flex items-center justify-between border border-[#FF5E1E]/40 bg-[#FF5E1E]/5 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0"><FileText size={18} className="text-[#FF5E1E] shrink-0" /><span className="text-sm truncate">{resume.name}</span></div>
                <button type="button" data-testid="resume-remove" onClick={() => setResume(null)} className="text-[#8FA3B8] hover:text-white"><X size={16} /></button>
              </div>
            ) : (
              <button type="button" data-testid="resume-dropzone" onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
                className="w-full border border-dashed border-white/20 hover:border-[#FF5E1E]/60 hover:bg-[#FF5E1E]/5 transition-colors px-4 py-6 flex flex-col items-center gap-2 group">
                <UploadCloud size={24} className="text-[#8FA3B8] group-hover:text-[#FF5E1E] transition-colors" />
                <span className="text-sm text-[#B8C4D0]">Drop your resume here or <span className="text-[#FF5E1E] font-medium">browse</span></span>
                <span className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8] uppercase">Get an instant AI job-match report</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden" data-testid="resume-input" onChange={(e) => pickFile(e.target.files?.[0])} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Full name"><Input data-testid="lead-form-name-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="ap-input" placeholder="John Rivera" /></F>
            <F label="Phone"><Input data-testid="lead-form-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="ap-input" placeholder="(555) 123-4567" /></F>
            <F label="Email"><Input data-testid="lead-form-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="ap-input" placeholder="john@email.com" /></F>
            <F label="Availability"><Input data-testid="lead-form-availability-input" value={form.availability} onChange={(e) => set("availability", e.target.value)} className="ap-input" placeholder="Immediate / 2 weeks" /></F>
            {!user && (
              <F label="Create a password"><Input data-testid="lead-form-password-input" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="ap-input" placeholder="Min 6 characters" /></F>
            )}
            <F label="Target track">
              <Select value={form.target_track} onValueChange={(v) => set("target_track", v)}>
                <SelectTrigger data-testid="lead-form-track-select" className="ap-input"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#10161F] border-white/15 text-white rounded-none">
                  {TRACK_OPTIONS.map((t) => <SelectItem key={t} value={t} className="focus:bg-[#FF5E1E] focus:text-[#0B0F14]">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Preferred shipyard / base">
              <Select value={form.preferred_location} onValueChange={(v) => set("preferred_location", v)}>
                <SelectTrigger data-testid="lead-form-location-select" className="ap-input"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#10161F] border-white/15 text-white rounded-none">
                  {BASES.map((b) => <SelectItem key={b} value={b} className="focus:bg-[#FF5E1E] focus:text-[#0B0F14]">{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Experience level">
              <Select value={form.experience_level} onValueChange={(v) => set("experience_level", v)}>
                <SelectTrigger data-testid="lead-form-experience-select" className="ap-input"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#10161F] border-white/15 text-white rounded-none">
                  {EXP.map((x) => <SelectItem key={x} value={x} className="focus:bg-[#FF5E1E] focus:text-[#0B0F14]">{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>

          {!user && (
            <p className="mt-3 flex items-center gap-2 text-[11px] text-[#8FA3B8] font-mono-tech tracking-wide"><Lock size={12} className="text-[#FF5E1E]" /> WE'LL CREATE YOUR FREE CANDIDATE PASSPORT SO YOUR APPLICATION & AI REPORT ARE SAVED.</p>
          )}

          <Button type="submit" disabled={busy} data-testid="lead-form-submit-button" className="mt-6 w-full rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase tracking-wide h-13 py-6 group">
            {busy ? "Submitting…" : <>{resume ? "Scan My Resume & Apply" : "Start My Application"} <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} /></>}
          </Button>

          <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-[#8FA3B8] font-mono-tech tracking-wide text-center">
            <ShieldCheck size={13} className="text-[#10B981]" /> NO CERTIFICATION, APPROVAL, OR EMPLOYMENT IS GUARANTEED BY APPLYING.
          </p>
        </motion.form>
      </div>

      <ScanningOverlay active={phase === "scanning"} />
      <ScanResultModal open={phase === "done"} result={result} firstName={firstName}
        onClose={() => setPhase("idle")} onDashboard={() => { setPhase("idle"); window.location.hash = "#dashboard"; }} />

      <style>{`
        .ap-input { background:#0B0F14; border:1px solid rgba(255,255,255,0.14); border-radius:0; color:#F4F6F8; height:2.75rem; }
        .ap-input:focus-visible, .ap-input:focus { outline:none; border-color:#FF5E1E; box-shadow:0 0 0 1px #FF5E1E; }
      `}</style>
    </section>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] uppercase mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
